/**
 * Card number patterns for different card types
 * @private
 */
const CARD_PATTERNS = {
    visa: /^4[0-9]{12}(?:[0-9]{3,6})?$/,
    mastercard: /^5[1-5][0-9]{14}$/,
    amex: /^3[47][0-9]{13}$/,
    discover: /^6(?:011|5[0-9]{2})[0-9]{12}$/,
    dinersClub: /^3(?:0[0-5]|[68][0-9])[0-9]{11,13}$/,
    jcb: /^(?:2131|1800|35\d{3})\d{11}$/
};
/**
 * Card format patterns with their corresponding regex for matching formated numbers
 * @private
 */
const FORMATED_CARD_PATTERNS = {
    visa: {
        pattern: /\b(4\d{15}|4\d{3}[\s-]*\d{4}[\s-]*\d{4}[\s-]*\d{4})\b/,
        validate: (num) => CARD_PATTERNS.visa.test(num)
    },
    mastercard: {
        pattern: /\b5[1-5]\d{2}[\s-]*\d{4}[\s-]*\d{4}[\s-]*\d{4}\b/,
        validate: (num) => CARD_PATTERNS.mastercard.test(num)
    },
    amex: {
        pattern: /\b3[47]\d{2}[\s-]*\d{6}[\s-]*\d{5}\b/,
        validate: (num) => CARD_PATTERNS.amex.test(num)
    },
    discover: {
        pattern: /\b6(?:011|5\d{2})[\s-]*\d{4}[\s-]*\d{4}[\s-]*\d{4}\b/,
        validate: (num) => CARD_PATTERNS.discover.test(num)
    }
};
/**
 * A preprocessor plugin that detects and anonymizes credit and debit card numbers in messages.
 * Validates common card number formats using Luhn algorithm and known patterns.
 * Replaces entire card number with 'X' characters.
 *
 * Supported card types:
 * - Visa (13, 16, 19 digits)
 * - Mastercard (16 digits)
 * - American Express (15 digits)
 * - Discover (16 digits)
 * - Diners Club (14, 16 digits)
 * - JCB (16 digits)
 *
 * @class CardNumberAnonymizer
 * @implements {PreprocessorPlugin}
 *
 * @example
 * ```typescript
 * const anonymizer = new CardNumberAnonymizer();
 * chatClient.addPreprocessorPlugin(anonymizer);
 * ```
 */
export class CardNumberAnonymizer {
    constructor() {
        /**
         * Unique identifier for the plugin
         */
        this.name = 'card-number-anonymizer';
    }
    /**
     * Validates a card number using the Luhn algorithm
     * @param cardNumber - The card number to validate
     * @returns boolean indicating if the card number is valid
     */
    isValidLuhn(cardNumber) {
        const digits = cardNumber.replace(/\D/g, '');
        let sum = 0;
        let isEven = false;
        // Loop through values starting from the rightmost digit
        for (let i = digits.length - 1; i >= 0; i--) {
            let digit = parseInt(digits[i]);
            if (isEven) {
                digit *= 2;
                if (digit > 9) {
                    digit -= 9;
                }
            }
            sum += digit;
            isEven = !isEven;
        }
        return sum % 10 === 0;
    }
    /**
     * Validates if the card number matches any known card pattern
     * @param cardNumber - The card number to validate
     * @returns boolean indicating if the card number matches a known pattern
     */
    matchesCardPattern(cardNumber) {
        const cleanNumber = cardNumber.replace(/\D/g, '');
        return Object.values(CARD_PATTERNS).some(pattern => pattern.test(cleanNumber));
    }
    /**
     * Processes a single pattern in the content and replaces matches with X's while preserving format
     * @param content - The content to process
     * @param pattern - The regex pattern to match
     * @param validate - The validation function for the card type
     * @returns The processed content with matches replaced by X's
     * @private
     */
    processPattern(content, pattern, validate) {
        return content.replace(pattern, (match, cardNumber) => {
            try {
                // If we have a capturing group, use it, otherwise use the full match
                const numberToProcess = match;
                // Clean the number for validation
                const cleanNumber = numberToProcess.replace(/[\s-]/g, '');
                // Validate both Luhn and card-specific pattern
                if (!this.isValidLuhn(cleanNumber) || !validate(cleanNumber)) {
                    return match;
                }
                // Create anonymized version preserving original format
                const anonymized = numberToProcess.replace(/\d/g, 'X');
                return anonymized;
            }
            catch (_a) {
                return match;
            }
        });
    }
    /**
     * Process a message to anonymize credit and debit card numbers.
     * Processes each card type separately to ensure accurate matching.
     *
     * @param {string} content - The message to process
     * @returns {Promise<string>} The message with anonymized card numbers
     */
    async process(content) {
        let processedContent = content;
        // Process each card type separately
        for (const { pattern, validate } of Object.values(FORMATED_CARD_PATTERNS)) {
            processedContent = this.processPattern(processedContent, pattern, validate);
        }
        return processedContent;
    }
}
//# sourceMappingURL=CardNumberAnonymizer.js.map